import { useInfiniteQuery } from '@tanstack/react-query';
import {
  AdminInput,
  AdminSpinner,
  BrandTemplateEntity,
  Button,
  Disclaimer,
  Div,
  EditPenIconRed,
  H5,
  Icon,
  Img,
  ModalPortal,
  P,
  Radio,
  getBrandTemplates,
  lightTheme,
  queryKeys,
  restaurantImage,
} from '@vgrubs/components';
import { ItemListImage } from 'components/src/adminPages/Menu/MenyStyled';
import 'flatpickr/dist/flatpickr.css';
import debouce from 'lodash.debounce';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

export const fetchBrandTemplates = async ({ pageParam = 1, query = '' }) => {
  const { data } = await getBrandTemplates({
    limit: 20,
    page: pageParam,
    query,
  });

  // If data length is less than the limit, we know there is no next page
  const nextPage = data.data.length === 20 ? pageParam + 1 : undefined;

  return {
    brandTemplates: data ?? [],
    nextPage,
  };
};

export const useInfiniteBrandTemplates = (query: string) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: queryKeys.brandTemplates.list(query),
    queryFn: ({ pageParam = 1 }) => fetchBrandTemplates({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

export const AdminBrandTemplatePickerModal = ({
  // selectedLocation,
  // setSelectedLocation,
  handleSubmit,
  filterActive,
  isOpen,
  onClose,
  changeParams,
}: {
  selectedLocation: BrandTemplateEntity | null;
  setSelectedLocation: Dispatch<SetStateAction<BrandTemplateEntity | null>>;
  handleSubmit: (location: BrandTemplateEntity) => void;
  filterActive: boolean;
  isOpen: boolean;
  onClose: () => void;
  changeParams: () => void;
}) => {
  const [searchParams] = useSearchParams();
  const brandTemplateName = searchParams.get('brandTemplateName');
  const [query, setQuery] = useState('');
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteBrandTemplates(query);
  const [hasMore, setHasMore] = useState(true);
  const [selected, setSelected] = useState<BrandTemplateEntity | null>();
  const setSelectedLocation = setSelected;
  const selectedLocation = selected;
  const observerRef = useRef<HTMLDivElement>(null);

  const handleSelectLocation = (newItem: BrandTemplateEntity) => {
    setSelectedLocation(newItem);
  };

  function isLocationSelected(item: BrandTemplateEntity) {
    return selectedLocation?.id === item.id;
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasMore(true);
    setQuery(e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  useEffect(() => {
    // Create a new intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        // If the target element is visible and there is more data to load
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage(); // Call the fetch for the next page
        }
      },
      {
        threshold: 1.0, // Trigger when 100% of the element is in view
      }
    );

    // Get the current target element (bottom observer element)
    const currentObserverTarget = observerRef.current;

    // Ensure that we have a valid target to observe
    if (currentObserverTarget) {
      observer.observe(currentObserverTarget); // Start observing
    }

    // Cleanup the observer on component unmount or before re-triggering
    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget); // Stop observing
      }
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]); // Re-run when these values change

  const templates = data?.pages
    .flatMap((item) => item.brandTemplates.data)
    .flat();

  return (
    <>
      {filterActive && brandTemplateName && (
        <Div $flex $alignCenter $gap="8px" $mb="16px" $mtSM="8">
          <P $m="0" style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}>
            <b>{brandTemplateName}</b>
          </P>{' '}
          <Icon $pointer onClick={changeParams} $sizeSM="28">
            <EditPenIconRed />
          </Icon>
        </Div>
      )}

      <ModalPortal
        isOpen={isOpen}
        header="Select Brand"
        overlayBorderRadius={30}
        logo={false}
        closeModal={onClose}
        footer={
          <>
            <Button
              $selected
              $w="100%"
              $wSM="100%"
              onClick={() => {
                // setModalPortalIsOpen((prev) => !prev);
                if (selected) {
                  handleSubmit(selected);
                }
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {isLoading && <AdminSpinner absolute small />}

        <Div $relative>
          <Div $mb="20">
            <AdminInput
              search
              onChange={debouncedResults}
              placeholder="Search Location"
            />
          </Div>
          <Div $minHeight="50vh">
            {templates && templates.length === 0 && !hasMore && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img
                  src={restaurantImage}
                  alt="restaurant"
                  $width="250px"
                  $widthSM="150px"
                />
                <H5>No Locations</H5>
              </Div>
            )}
            {templates &&
              templates.map((lc, i) => {
                const l = lc;
                return (
                  <Div
                    key={i}
                    $box
                    $p="14px 24px"
                    $mb="8"
                    $flex
                    $alignCenter
                    $between
                    onClick={() => handleSelectLocation(l)}
                    $pointer
                    // delay={0.1}
                  >
                    <Div $flex $alignCenter $gap="8px">
                      <ItemListImage>
                        <Img alt="" src={l.imageUrl} $width="60px" />
                      </ItemListImage>
                      <div>
                        <P
                          $left
                          $bold
                          $mb="8"
                          $color={isLocationSelected(l) && lightTheme.primary}
                          $lineHeight="1.2"
                        >
                          {l.name}
                        </P>
                        <Disclaimer $left $mb="0">
                          {l.phoneNumber}
                        </Disclaimer>
                      </div>
                    </Div>

                    <Radio checked={isLocationSelected(l)} />
                  </Div>
                );
              })}
            <Div ref={observerRef} $height="10px" $heightSM="10px" />
          </Div>
        </Div>
      </ModalPortal>
    </>
  );
};
